import {SearchHelpers} from "./search-helper";
import {get, defaults, assign} from "lodash";
import {toNumber, toString} from "./helpers";
import {FormGroup} from "@angular/forms";
import {QuickFormArray} from "./quick-form-group";
import {JSONFormGroup} from "../json-shared.model";
import {JSONModel} from "../json.model";

export class BooleanFilter<T> {
  check: (item: T, value: boolean) => boolean;
  value: boolean;
  caption: string;
}

export type SearchKeyType = string | ((item: any) => any);

export interface FilteredListOptions<T> {
  items?: T[];
  searchKeys?: string[];
  exactMatchSearchKeys?: string[];
  startingPage?: number;
  startingPageSize?: number;
  formKeys?: string[];
  booleanFilters?: BooleanFilter<T>[];
  indexField?: string;
}

function getFilteredListDefaults<T>(): FilteredListOptions<T> {
  return {
    items: [],
    searchKeys: [],
    exactMatchSearchKeys: [],
    startingPage: 1,
    startingPageSize: 10,
    formKeys: [],
    booleanFilters: [],
    indexField: undefined,
  };
}

function getValues(item: any, keys: string[]): string[] {
  return keys.map((key: string) => get(item as any, key))
    .map((value) => {
      if (value === null || value === undefined) return value;
      return `${value}`;
    })
    .filter(value => !!value);
}

export class FilteredList<T> extends JSONModel implements JSONFormGroup {
  private all: T[] = [];
  searchFiltered: T[] = [];
  filtered: T[] = [];
  private searchFilteredIndexes: number[];

  currentPage: number = 1;
  pageSize: number = 10;
  maxPages: number;
  searchText: string = "";
  searchKeys: string[] = [];
  exactMatchSearchKeys: string[] = [];
  booleanFilters: BooleanFilter<T>[] = [];
  form: FormGroup;
  indexField: string = undefined;
  hasAnyItems: boolean = false;
  hasFilteredItems: boolean = false;

  get filteredLength(): number {
    return this.searchFiltered.length;
  }

  get items(): T[] {
    return this.all;
  }

  set items(items: T[]) {
    this.all = items;
    this.updateFormGroup();
    this.updateAll();
    this.hasAnyItems = items.length > 0;
  }

  constructor(private readonly options: FilteredListOptions<T> = {}) {
    super();

    this.options = assign({}, this.options);
    this.options = defaults(this.options, getFilteredListDefaults());

    this.currentPage = this.options.startingPage;
    this.pageSize = this.options.startingPageSize;
    this.searchKeys = this.options.searchKeys;
    this.exactMatchSearchKeys = this.options.exactMatchSearchKeys;
    this.booleanFilters = this.options.booleanFilters;
    this.items = this.options.items;
    this.indexField = this.options.indexField;
  }

  private updateFormGroup() {
    if (this.options.formKeys && this.options.formKeys.length) {
      this.form = QuickFormArray.buildGroup(this.items, this.options.formKeys);
      this.form.addControl("booleanFilters", new QuickFormArray(this.booleanFilters, ["value"]));
    } else if (this.booleanFilters && this.booleanFilters.length) {
      this.form = new FormGroup({});
      this.form.addControl("booleanFilters", new QuickFormArray(this.booleanFilters, ["value"]));
    }
  }

  private updatePaginated() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.filtered = this.searchFiltered.slice(start, end);

    this.maxPages = Math.ceil(this.searchFiltered.length / (+this.pageSize));
  }

  private updateSearchFiltered() {
    this.searchFilteredIndexes = [];

    this.searchFiltered = this.all.filter((item, index) => {
      if (!item) return false;

      for (const filter of this.booleanFilters) {
        if (!filter.check(item, filter.value)) return false;
      }

      if (!this.searchKeys.length && !this.exactMatchSearchKeys.length) return true;

      if (this.searchText) {
        const approximateSearchValues: string[] = getValues(item, this.searchKeys);
        const exactSearchValues: string[] = getValues(item, this.exactMatchSearchKeys);

        const searchMatched = SearchHelpers.tokenizeString(this.searchText)
          .map(v => SearchHelpers.stripQuotes(v))
          .map(
            token => (
              !token ||
              exactSearchValues.some(v => token === v) ||
              approximateSearchValues.some(v => SearchHelpers.makeRegExp(token).test(v))
            ),
          )
          .every(it => it === true);

        if (!searchMatched) return false;
      }

      this.searchFilteredIndexes.push(index);

      return true;
    });

    this.hasFilteredItems = this.searchFiltered.length > 0;
  }

  private updateAll() {
    this.updateSearchFiltered();
    this.updatePaginated();
  }

  refreshDisplay() {
    this.updateAll();
  }

  onListControlsChange(event: any) {
    event = event || {};

    switch (event["key"]) {
      case "search":
        this.currentPage = this.options.startingPage;
        this.searchText = event["value"];
        this.updateAll();
        break;
      case "page":
        this.currentPage = event["value"];
        this.updatePaginated();
        break;
      case "limit":
        this.currentPage = this.options.startingPage;
        this.pageSize = event["value"];
        this.updatePaginated();
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
  }

  booleanFilterChanged() {
    this.updateAll();
  }

  setParams(currentPage: any, pageSize: any, searchText: any) {
    currentPage = toNumber(currentPage, this.options.startingPage);
    pageSize = toNumber(pageSize, this.options.startingPageSize);
    searchText = toString(searchText);

    let updateAll = false;
    let updatePaginated = false;

    if (currentPage !== this.currentPage) {
      updatePaginated = true;
      this.currentPage = currentPage;
    }

    if (pageSize !== this.pageSize) {
      updatePaginated = true;
      this.pageSize = pageSize;
      this.currentPage = this.options.startingPage;
    }

    if (searchText !== this.searchText) {
      updateAll = true;
      this.searchText = searchText;
      this.currentPage = this.options.startingPage;
    }

    if (updateAll) {
      this.updateAll();
    } else if (updatePaginated) {
      this.updatePaginated();
    }
  }

  setSearchText(searchText: any) {
    searchText = toString(searchText);

    if (searchText !== this.searchText) {
      this.searchText = searchText;
      this.currentPage = this.options.startingPage;
      this.updateAll();
    }
  }

  forEachItem(delegate: (formGroup: FormGroup, item: T, index: number) => void) {
    const groups = this.form.controls["items"]["controls"] as FormGroup[];

    for (let index = 0; index < this.items.length; index++) {
      const item = this.items[index];
      const formGroup = groups[index];

      delegate(formGroup, item, index);
    }
  }

  forEachSearchFiltered(delegate: (formGroup: FormGroup, item: T, index: number) => void) {
    const groups = this.form.controls["items"]["controls"] as FormGroup[];

    for (const index of this.searchFilteredIndexes) {
      const item = this.items[index];
      const formGroup = groups[index];

      delegate(formGroup, item, index);
    }
  }

  private updateIndexes() {
    if (!this.indexField) return;

    this.all.forEach((item, index) => {
      item[this.indexField] = index as any;
    });

    const groups = this.form.controls["items"]["controls"] as FormGroup[];

    groups.forEach((item, index) => {
      if (item.controls[this.indexField]) {
        item.controls[this.indexField].setValue(index as any);
      }
    });
  }

  insertForAdd(item?: T): T {
    const index = (this.currentPage - 1) * this.pageSize;

    this.all.splice(index, 0, item);
    this.filtered.splice(0, 0, item);

    if (this.indexField) {
      item[this.indexField] = (index - 0.1) as any;
    }

    const formArray = this.form.controls["items"] as QuickFormArray;
    const formGroup = formArray.buildNewFormGroup(item);
    formArray.insert(index, formGroup);
    formGroup.markAsDirty();

    this.updateIndexes();

    return item;
  }

  remove(item?: any) {
    const index = item ? this.all.indexOf(item) : -1;

    if (index < 0 || index >= this.all.length) return;

    this.all.splice(index, 1);

    const fa = this.form.controls["items"] as QuickFormArray;
    fa.removeAt(index);
    fa.markAsDirty();

    this.updateIndexes();

    this.updateAll();
  }

  move(item: any, direction: number) {
    if (direction === 0) return;

    const thisIndex = this.all.indexOf(item);
    const otherIndex = thisIndex + direction;

    if (thisIndex < 0) return;
    if (otherIndex < 0 || otherIndex >= this.all.length) return;

    [this.all[otherIndex], this.all[thisIndex]] = [this.all[thisIndex], this.all[otherIndex]];

    const formArray: QuickFormArray = this.form.controls["items"] as QuickFormArray;
    const formGroup = formArray.controls[thisIndex];

    formGroup.markAsDirty();

    formArray.removeAt(thisIndex);
    formArray.insert(otherIndex, formGroup);

    this.updateIndexes();

    this.updateAll();
  }
}
